/**
 * <Modal> — Blocking dialog for confirmations, forms, upsells.
 *
 * Built on <Overlay> with preset zIndexToken='modal' (1200), modal-specific
 * sizing, standardized structure (Header/Body/Footer), and a backdrop.
 *
 * Composition-based API:
 *
 *   <Modal.Root open={...} onOpenChange={...}>
 *     <Modal.Trigger asChild>
 *       <Button>Open</Button>
 *     </Modal.Trigger>
 *     <Modal.Content size="md" title="Confirm action">
 *       <Modal.Description>Are you sure?</Modal.Description>
 *       ...body...
 *       <Modal.Footer>
 *         <Modal.Close asChild>
 *           <Button variant="secondary">Cancel</Button>
 *         </Modal.Close>
 *         <Button onClick={handleConfirm}>Confirm</Button>
 *       </Modal.Footer>
 *     </Modal.Content>
 *   </Modal.Root>
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.2
 */

import React, { useId } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useOverlayPortal } from './useOverlayPortal';

// Size presets (max-width)
const SIZE_CLASSES = {
  sm: 'max-w-[400px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  full: 'max-w-[95vw]',
};

/**
 * Modal.Root — state + context holder. Thin wrapper over Radix Dialog.Root.
 */
function ModalRoot({ open, defaultOpen, onOpenChange, children }) {
  return (
    <Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {children}
    </Dialog.Root>
  );
}
ModalRoot.displayName = 'Modal.Root';

/**
 * Modal.Trigger — button or element that opens the modal.
 * Use asChild to merge props onto a child element.
 */
const ModalTrigger = Dialog.Trigger;

/**
 * Modal.Close — closes the modal. Use asChild to merge onto custom button.
 */
const ModalClose = Dialog.Close;

/**
 * Modal.Content — main container.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {'sm'|'md'|'lg'|'full'} [props.size='md']
 * @param {string} [props.title] - If provided, renders as Modal.Title automatically
 * @param {boolean} [props.dismissible=true] - Click outside / Escape closes
 * @param {string} [props.className]
 * @param {string} [props.ariaLabelledBy] - Explicit labelledby; overrides title
 * @param {string} [props.ariaDescribedBy]
 * @param {(e: Event) => void} [props.onInteractOutside]
 * @param {(e: Event) => void} [props.onEscapeKeyDown]
 */
function ModalContent({
  children,
  size = 'md',
  title,
  dismissible = true,
  className = '',
  ariaLabelledBy,
  ariaDescribedBy,
  onInteractOutside,
  onEscapeKeyDown,
  ...rest
}) {
  const portalContainer = useOverlayPortal();
  const autoTitleId = useId();

  // Derive aria-labelledby: explicit prop wins, else auto-generated if title provided
  const effectiveLabelledBy = ariaLabelledBy || (title ? autoTitleId : undefined);

  // Dev warning if no accessible name
  if (process.env.NODE_ENV !== 'production') {
    if (!title && !ariaLabelledBy) {
      console.warn(
        '[Modal.Content] No title prop or ariaLabelledBy. ' +
        'Screen readers require an accessible name. ' +
        'Provide title="..." or use <Modal.Title id="..."> and pass ariaLabelledBy.'
      );
    }
  }

  const handleInteractOutside = (e) => {
    if (!dismissible) {
      e.preventDefault();
    }
    if (onInteractOutside) onInteractOutside(e);
  };

  const handleEscapeKeyDown = (e) => {
    if (!dismissible) {
      e.preventDefault();
    }
    if (onEscapeKeyDown) onEscapeKeyDown(e);
  };

  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  return (
    <Dialog.Portal container={portalContainer}>
      <Dialog.Overlay
        className="fixed inset-0 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        style={{ zIndex: 'var(--aif-z-modal)' }}
      />
      <Dialog.Content
        className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] ${sizeClass} max-h-[calc(100vh-32px)] overflow-y-auto bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] rounded-[var(--aif-radius-lg)] shadow-[var(--aif-shadow-elevated)] border border-[var(--aif-border-default)] focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 ${className}`}
        style={{ zIndex: 'var(--aif-z-modal)' }}
        aria-labelledby={effectiveLabelledBy}
        aria-describedby={ariaDescribedBy}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...rest}
      >
        {title && (
          <div className="px-6 pt-6 pb-4">
            <Dialog.Title id={autoTitleId} className="text-lg font-semibold text-[var(--aif-text-primary)]">
              {title}
            </Dialog.Title>
          </div>
        )}
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  );
}
ModalContent.displayName = 'Modal.Content';

/**
 * Modal.Title — explicit title element (use when title prop on Content not sufficient).
 */
function ModalTitle({ children, className = '', ...rest }) {
  return (
    <Dialog.Title
      className={`text-lg font-semibold text-[var(--aif-text-primary)] ${className}`}
      {...rest}
    >
      {children}
    </Dialog.Title>
  );
}
ModalTitle.displayName = 'Modal.Title';

/**
 * Modal.Description — accessible description, paired with aria-describedby.
 */
function ModalDescription({ children, className = '', ...rest }) {
  return (
    <Dialog.Description
      className={`text-sm text-[var(--aif-text-secondary)] mb-4 ${className}`}
      {...rest}
    >
      {children}
    </Dialog.Description>
  );
}
ModalDescription.displayName = 'Modal.Description';

/**
 * Modal.Body — main scrollable content area.
 */
function ModalBody({ children, className = '' }) {
  return (
    <div className={`px-6 pb-4 ${className}`}>
      {children}
    </div>
  );
}
ModalBody.displayName = 'Modal.Body';

/**
 * Modal.Footer — action row at the bottom.
 */
function ModalFooter({ children, className = '' }) {
  return (
    <div className={`px-6 py-4 border-t border-[var(--aif-border-default)] flex justify-end gap-3 ${className}`}>
      {children}
    </div>
  );
}
ModalFooter.displayName = 'Modal.Footer';

// Public API — composition-based
export const Modal = {
  Root: ModalRoot,
  Trigger: ModalTrigger,
  Close: ModalClose,
  Content: ModalContent,
  Title: ModalTitle,
  Description: ModalDescription,
  Body: ModalBody,
  Footer: ModalFooter,
};
